import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Loader2 } from 'lucide-react';
import useModuleEnabled from '@/hooks/useModuleEnabled';

// ── ModuleGate ───────────────────────────────────────────────────────────────
// Route/surface gate for a sellable van-module, sourced from the entitlement
// projection (useModuleEnabled → public.active_module_keys) rather than a
// build-time flag. Mirrors how FIELD_ASSIST_ENABLED wraps its routes, but the
// on/off decision is per-org and live.
//
// For the flagship org 'parcelink' this is a NO-OP: useModuleEnabled fails safe
// to enabled (both while loading and on error), so `children` render exactly as
// they do today. Gating only bites a second tenant that has not purchased the
// module — they see a quiet "not on your plan" surface instead of the feature.
//
// NOTE: this gates the SURFACE only (the page + its nav entry). It deliberately
// does not touch the underlying protocol/alarm plumbing (ProtocolAlarmManager,
// step events), so nothing a current user relies on is severed.
export default function ModuleGate({ module, org, children, fallback = null }) {
  const { enabled, loaded } = useModuleEnabled(module, org);

  if (enabled) return children;

  // Not enabled. If we haven't loaded yet this branch is only reachable for a
  // non-flagship org (flagship fails safe to enabled), so a brief spinner is
  // correct rather than flashing the locked state.
  if (!loaded) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-10 w-10 text-primary animate-spin" />
      </div>
    );
  }

  if (fallback) return fallback;

  return (
    <div className="text-center py-12 surface-card border-dashed">
      <Lock className="mx-auto h-14 w-14 text-muted-foreground mb-4 opacity-60" />
      <h3 className="text-xl font-semibold text-foreground">Not on your plan</h3>
      <p className="text-muted-foreground mt-1">
        This module isn&rsquo;t included for your organisation yet.
      </p>
      <Link
        to="/subcontractor/dashboard"
        className="mt-4 inline-flex items-center rounded-2xl bg-primary px-4 py-2 text-sm font-medium text-white shadow-glow"
      >
        Back to dashboard
      </Link>
    </div>
  );
}
